import { PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import SplashScreen from "@/components/SplashScreen";
import { useCurrentPlatformContext } from "@/contexts";
import { captureSentryException } from "@/lib/sentry";
import { TerminologySettings } from "@/types/terminology.types";

import { isLanguage, Language } from "./i18n.types";
import { applyTerminologyOverrides, TerminologyOverrideOptions } from "./terminologyOverrides";

/**
 * Derives the active (non-default) terminology override options from the current platform's stored
 * terminology config. `null` (no config) or a term left at its default keeps the base wording.
 */
const getOverrideOptions = (
  settings: TerminologySettings | null | undefined
): TerminologyOverrideOptions => {
  if (!settings) return {};

  const options: TerminologyOverrideOptions = {};
  if (settings.tournament !== "tournament") {
    options.tournament = settings.tournament;
  }
  return options;
};

/**
 * Applies the current platform's terminology overrides whenever the platform or
 * the active language changes. Mounted just inside `CurrentPlatformProvider` so
 * `platform` is resolved.
 *
 * It owns LOCAL readiness state (not the shared i18n loading store): it renders
 * its own splash until the first application completes, then renders children.
 * Reusing the global store here would deadlock — this gate lives inside
 * `I18nSplashScreenProvider`, so flipping that store would unmount the gate
 * before it could clear the flag. Subsequent platform/language switches re-apply
 * in the background (no splash) and let `emit("languageChanged")` re-render.
 */
export const TerminologyOverridesGate = ({ children }: PropsWithChildren) => {
  const { platform, loading } = useCurrentPlatformContext();
  const { i18n } = useTranslation();
  const language = i18n.language;

  const [ready, setReady] = useState(false);

  useEffect(() => {
    const lng: Language | null = isLanguage(language) ? language : null;
    if (lng === null) {
      setReady(true);
      return;
    }

    // Wait for the current platform to resolve before the FIRST application so
    // children paint with the right terminology already in place. Otherwise they
    // mount with base wording and only update via the re-apply's re-render — which
    // persistent components (e.g. the sidebar nav) can miss, leaving them stale.
    if (loading && !ready) return;

    let cancelled = false;
    const options = getOverrideOptions(platform?.terminology);

    applyTerminologyOverrides(lng, options)
      .catch((err) => {
        captureSentryException(err, {
          tags: { source: "i18n.applyTerminologyOverrides" },
          extra: { locale: lng, platformId: platform?.id },
        });
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
    // Re-apply when the platform (incl. its stored terminology), its load state, or the language
    // changes. `platform` is memoized in CurrentPlatformProvider, so `terminology`'s identity is
    // stable between data fetches and this won't loop.
  }, [loading, ready, platform?.id, platform?.terminology, language]);

  if (!ready) {
    return <SplashScreen className="bg-surface-1" />;
  }

  return <>{children}</>;
};
